import {
  ErrorEventSeverity,
  SeverityCatalogEntry,
  SeverityGroup,
  SeverityGroupRule,
  SeverityIdentifierRule,
  SeverityRulesResponse,
} from '../../../services/api';

/**
 * Severity-Auflösung im Frontend — spiegelt die Reihenfolge des Error-Processors.
 *
 * Die effektive Severity wird bewusst clientseitig berechnet: nur so lässt sich
 * die Wirkung noch nicht gespeicherter Änderungen (Sammel-Speichern) sofort
 * anzeigen, und das Herkunfts-Popover kann jede Stufe einzeln begründen.
 *
 * Merkregel der Reihenfolge: Kunde schlägt Global, und innerhalb eines Scopes
 * schlägt eine service-genaue Identifier-Regel die Service-Wildcard, und die
 * Wildcard wiederum die Gruppen-Regel.
 */

/** Severity, die greift, wenn keine einzige Regel zutrifft. */
export const SEVERITY_FALLBACK: ErrorEventSeverity = 'medium';

/** Die sieben Stufen der Auflösung, in Vorrangreihenfolge. */
export type SeverityRuleLevel =
  | 'customer_identifier_service'
  | 'customer_identifier_any'
  | 'customer_group'
  | 'global_identifier_service'
  | 'global_identifier_any'
  | 'global_group'
  | 'fallback';

/** Geltungsbereich der Bearbeitung — `global` gilt für alle Kunden ohne eigenen Override. */
export type SeverityScope = 'global' | 'customer';

/** Eine ausgewertete Stufe der Auflösung — Grundlage des Herkunfts-Popovers. */
export interface SeverityResolutionStep {
  level: SeverityRuleLevel;
  /** Deutsche Bezeichnung der Stufe, z.B. „Kunde × Identifier (Alle Services)". */
  label: string;
  /** Begründung, warum die Stufe greift oder nicht (Gruppenname, „keine Regel hinterlegt", …). */
  detail: string;
  /** Severity, die diese Stufe beisteuern würde — `null`, wenn keine Regel hinterlegt ist. */
  severity: ErrorEventSeverity | null;
  /** `true`, wenn die Stufe im aktuellen Geltungsbereich gar nicht greifen kann. */
  notApplicable: boolean;
  /** Genau eine Stufe je Auflösung ist der Treffer. */
  isWinner: boolean;
}

/** Ergebnis der Auflösung samt vollständiger Begründungskette. */
export interface SeverityResolution {
  severity: ErrorEventSeverity;
  winningLevel: SeverityRuleLevel;
  steps: SeverityResolutionStep[];
}

/**
 * Alle Identifier-Regeln zu einem `errorIdentifier` innerhalb eines Scopes,
 * getrennt nach Wildcard und service-genauen Regeln.
 */
export interface SeverityIdentifierRuleBucket {
  /** Regel ohne Service-Bindung (`service: null`) — gilt für jeden Sender. */
  wildcard: SeverityIdentifierRule | null;
  /** Service-genaue Regeln, Schlüssel = Service. */
  byService: Map<string, SeverityIdentifierRule>;
}

/**
 * Nach Schlüssel aufgelöste Regeln beider Geltungsbereiche.
 * Identifier-Buckets sind nach `errorIdentifier` indiziert, Gruppen-Regeln nach `groupId`.
 */
export interface SeverityRuleIndex {
  globalIdentifier: Map<string, SeverityIdentifierRuleBucket>;
  globalGroup: Map<string, SeverityGroupRule>;
  customerIdentifier: Map<string, SeverityIdentifierRuleBucket>;
  customerGroup: Map<string, SeverityGroupRule>;
}

/** Leerer Bucket — vermeidet Null-Prüfungen an den Auswertungsstellen. */
const EMPTY_BUCKET: SeverityIdentifierRuleBucket = { wildcard: null, byService: new Map() };

/**
 * Noch nicht gespeicherte Änderungen des aktiven Geltungsbereichs.
 * `null` als Severity bedeutet „Regel entfernen" (erben statt überschreiben).
 */
export interface SeverityPendingChanges {
  /** groupId → Severity oder `null` (Regel entfernen). */
  groupRules: Record<string, ErrorEventSeverity | null>;
  /** errorIdentifier → Severity oder `null` (Regel entfernen). */
  identifierRules: Record<string, ErrorEventSeverity | null>;
  /** errorIdentifier → groupId oder `null`. Gruppen-Zuordnung ist nicht kundenspezifisch. */
  catalogGroups: Record<string, string | null>;
  /**
   * errorIdentifier → Service, auf den die Regel verfeinert werden soll.
   * `null` = Service-Wildcard („Alle Services"), der Normalfall.
   */
  ruleServices: Record<string, string | null>;
}

export const EMPTY_PENDING_CHANGES: SeverityPendingChanges = {
  groupRules: {},
  identifierRules: {},
  catalogGroups: {},
  ruleServices: {},
};

/** Anzahl der Änderungen im Puffer — Grundlage für den Zähler in der Save-Bar. */
export function countPendingChanges(pending: SeverityPendingChanges): number {
  return (
    Object.keys(pending.groupRules).length +
    Object.keys(pending.identifierRules).length +
    Object.keys(pending.catalogGroups).length
  );
}

/**
 * Baut den Regel-Index aus den Server-Antworten beider Geltungsbereiche.
 *
 * Die Partitionierung erfolgt über das `customerId`-Feld der Regel selbst, nicht
 * über die Herkunft der Antwort — so ist es egal, ob der Server bei
 * `?customerId=` zusätzlich die globalen Regeln mitliefert.
 */
export function buildSeverityRuleIndex(
  globalRules: SeverityRulesResponse | null,
  customerRules: SeverityRulesResponse | null
): SeverityRuleIndex {
  const index: SeverityRuleIndex = {
    globalIdentifier: new Map(),
    globalGroup: new Map(),
    customerIdentifier: new Map(),
    customerGroup: new Map(),
  };

  const bucketFor = (
    target: Map<string, SeverityIdentifierRuleBucket>,
    errorIdentifier: string
  ): SeverityIdentifierRuleBucket => {
    const existing = target.get(errorIdentifier);
    if (existing) return existing;
    const created: SeverityIdentifierRuleBucket = { wildcard: null, byService: new Map() };
    target.set(errorIdentifier, created);
    return created;
  };

  const ingest = (rules: SeverityRulesResponse | null) => {
    if (!rules) return;
    for (const rule of rules.identifierRules) {
      const target = rule.customerId === null ? index.globalIdentifier : index.customerIdentifier;
      const bucket = bucketFor(target, rule.errorIdentifier);
      if (rule.service === null) {
        bucket.wildcard ??= rule;
      } else if (!bucket.byService.has(rule.service)) {
        bucket.byService.set(rule.service, rule);
      }
    }
    for (const rule of rules.groupRules) {
      const target = rule.customerId === null ? index.globalGroup : index.customerGroup;
      if (!target.has(rule.groupId)) target.set(rule.groupId, rule);
    }
  };

  ingest(globalRules);
  ingest(customerRules);
  return index;
}

/** Identifier-Bucket eines Scopes — nie `undefined`, im Zweifel leer. */
export function identifierBucket(
  index: SeverityRuleIndex,
  scope: SeverityScope,
  errorIdentifier: string
): SeverityIdentifierRuleBucket {
  const source = scope === 'customer' ? index.customerIdentifier : index.globalIdentifier;
  return source.get(errorIdentifier) ?? EMPTY_BUCKET;
}

/** Gesamtzahl der Regeln im Bucket — Grundlage für den Mehrfach-Regeln-Hinweis. */
export function countBucketRules(bucket: SeverityIdentifierRuleBucket): number {
  return bucket.byService.size + (bucket.wildcard ? 1 : 0);
}

/**
 * Die Regel, die eine Katalog-Zeile repräsentiert: die spezifischste vorhandene.
 *
 * Reihenfolge: service-genaue Regel zu einem beobachteten Sender → irgendeine
 * service-genaue Regel → Wildcard. `null`, wenn im Scope gar keine Regel liegt.
 */
export function pickIdentifierRule(
  bucket: SeverityIdentifierRuleBucket,
  services: string[]
): SeverityIdentifierRule | null {
  for (const service of services) {
    const match = bucket.byService.get(service);
    if (match) return match;
  }
  const [firstExact] = bucket.byService.values();
  return firstExact ?? bucket.wildcard;
}

/**
 * Service, auf den die Regel der Zeile verfeinert ist.
 *
 * `null` = Service-Wildcard („Alle Services") — das ist der Standard für neue
 * Regeln, seit Identifier-Regeln ohne Service-Bindung möglich sind.
 */
export function resolveRuleService(
  entry: SeverityCatalogEntry,
  bucket: SeverityIdentifierRuleBucket,
  pending: SeverityPendingChanges
): string | null {
  if (entry.errorIdentifier in pending.ruleServices) {
    return pending.ruleServices[entry.errorIdentifier];
  }
  return pickIdentifierRule(bucket, entry.services)?.service ?? null;
}

/** Gruppen-Zuordnung eines Katalog-Eintrags inklusive Puffer-Überlagerung. */
export function effectiveGroupId(
  entry: SeverityCatalogEntry,
  pending: SeverityPendingChanges
): string | null {
  return entry.errorIdentifier in pending.catalogGroups
    ? pending.catalogGroups[entry.errorIdentifier]
    : entry.groupId;
}

/** Severity einer Gruppen-Ebene inklusive Puffer-Überlagerung. */
function groupSeverity(
  groupId: string | null,
  rules: Map<string, SeverityGroupRule>,
  pendingForLevel: Record<string, ErrorEventSeverity | null> | null
): ErrorEventSeverity | null {
  if (groupId === null) return null;
  if (pendingForLevel && groupId in pendingForLevel) return pendingForLevel[groupId];
  return rules.get(groupId)?.severity ?? null;
}

/** Severity der beiden Identifier-Stufen eines Scopes, inklusive Puffer. */
interface IdentifierSeverities {
  /** Service-genaue Stufe — `null`, wenn die Zeile auf Wildcard steht oder keine Regel existiert. */
  exact: ErrorEventSeverity | null;
  /** Wildcard-Stufe. */
  wildcard: ErrorEventSeverity | null;
}

/**
 * Wertet die Identifier-Stufen eines Scopes aus.
 *
 * Der Puffer wirkt immer auf genau die Stufe, die die Zeile gerade
 * repräsentiert: bei gesetztem `ruleService` auf die service-genaue Stufe,
 * sonst auf die Wildcard. Ein Wechsel des Verfeinerungs-Service verschiebt die
 * Regel (die alte wird beim Speichern gelöscht) — deshalb blendet die
 * Wildcard-Ansicht eine bestehende service-genaue Regel bewusst aus.
 */
function identifierSeverities(
  bucket: SeverityIdentifierRuleBucket,
  ruleService: string | null,
  pendingSeverity: ErrorEventSeverity | null | undefined
): IdentifierSeverities {
  const hasPending = pendingSeverity !== undefined;

  if (ruleService === null) {
    return {
      exact: null,
      wildcard: hasPending ? pendingSeverity : (bucket.wildcard?.severity ?? null),
    };
  }

  return {
    exact: hasPending ? pendingSeverity : (bucket.byService.get(ruleService)?.severity ?? null),
    wildcard: bucket.wildcard?.severity ?? null,
  };
}

export interface ResolveSeverityParams {
  entry: SeverityCatalogEntry;
  index: SeverityRuleIndex;
  pending: SeverityPendingChanges;
  scope: SeverityScope;
  /** Gruppen nach ID, nur für die Begründungstexte. */
  groupsById: Map<string, SeverityGroup>;
  /** Service, auf den die Regel der Zeile verfeinert ist — `null` = Wildcard. */
  ruleService: string | null;
}

/**
 * Löst die effektive Severity eines Katalog-Eintrags auf und liefert die
 * vollständige Begründungskette mit.
 *
 * Reihenfolge: 1. Kunde × Identifier (Service exakt) → 2. Kunde × Identifier
 * (Alle Services) → 3. Kunde × Gruppe → 4. Global × Identifier (Service exakt)
 * → 5. Global × Identifier (Alle Services) → 6. Global × Gruppe
 * → 7. Rückfallwert `medium`.
 */
export function resolveSeverity({
  entry,
  index,
  pending,
  scope,
  groupsById,
  ruleService,
}: ResolveSeverityParams): SeverityResolution {
  const isCustomerScope = scope === 'customer';
  const groupId = effectiveGroupId(entry, pending);
  const groupName = groupId ? (groupsById.get(groupId)?.name ?? 'Unbekannte Gruppe') : null;

  const pendingSeverity =
    entry.errorIdentifier in pending.identifierRules
      ? pending.identifierRules[entry.errorIdentifier]
      : undefined;

  // Der Puffer gehört immer zum aktiven Geltungsbereich.
  const customerIdentifier = isCustomerScope
    ? identifierSeverities(
        identifierBucket(index, 'customer', entry.errorIdentifier),
        ruleService,
        pendingSeverity
      )
    : { exact: null, wildcard: null };

  const globalIdentifier = identifierSeverities(
    identifierBucket(index, 'global', entry.errorIdentifier),
    ruleService,
    isCustomerScope ? undefined : pendingSeverity
  );

  const customerGroupSeverity = isCustomerScope
    ? groupSeverity(groupId, index.customerGroup, pending.groupRules)
    : null;
  const globalGroupSeverity = groupSeverity(
    groupId,
    index.globalGroup,
    isCustomerScope ? null : pending.groupRules
  );

  const outOfScope = 'Im globalen Geltungsbereich nicht anwendbar';
  const noRule = 'Keine Regel hinterlegt';
  const noGroup = 'Identifier ist keiner Gruppe zugeordnet';
  const wildcardOnly = 'Regel gilt für alle Services';

  const groupDetail = (severity: ErrorEventSeverity | null, suffix: string) =>
    groupName === null ? noGroup : severity ? `${groupName}${suffix}` : `${groupName} · ${noRule}`;

  const candidates: Omit<SeverityResolutionStep, 'isWinner'>[] = [
    {
      level: 'customer_identifier_service',
      label: 'Kunde × Identifier (Service exakt)',
      detail: !isCustomerScope
        ? outOfScope
        : ruleService === null
          ? wildcardOnly
          : customerIdentifier.exact
            ? `Kundenspezifisch für ${ruleService}`
            : `${noRule} für ${ruleService}`,
      severity: customerIdentifier.exact,
      notApplicable: !isCustomerScope || ruleService === null,
    },
    {
      level: 'customer_identifier_any',
      label: 'Kunde × Identifier (Alle Services)',
      detail: !isCustomerScope
        ? outOfScope
        : customerIdentifier.wildcard
          ? 'Kundenspezifische Wildcard-Regel'
          : noRule,
      severity: customerIdentifier.wildcard,
      notApplicable: !isCustomerScope,
    },
    {
      level: 'customer_group',
      label: 'Kunde × Gruppe',
      detail: !isCustomerScope ? outOfScope : groupDetail(customerGroupSeverity, ' · Override'),
      severity: customerGroupSeverity,
      notApplicable: !isCustomerScope,
    },
    {
      level: 'global_identifier_service',
      label: 'Global × Identifier (Service exakt)',
      detail:
        ruleService === null
          ? wildcardOnly
          : globalIdentifier.exact
            ? `Globale Regel für ${ruleService}`
            : `${noRule} für ${ruleService}`,
      severity: globalIdentifier.exact,
      notApplicable: ruleService === null,
    },
    {
      level: 'global_identifier_any',
      label: 'Global × Identifier (Alle Services)',
      detail: globalIdentifier.wildcard ? 'Globale Wildcard-Regel' : noRule,
      severity: globalIdentifier.wildcard,
      notApplicable: false,
    },
    {
      level: 'global_group',
      label: 'Global × Gruppe',
      detail: groupDetail(globalGroupSeverity, ''),
      severity: globalGroupSeverity,
      notApplicable: false,
    },
    {
      level: 'fallback',
      label: 'Rückfallwert',
      detail: 'Greift nur, wenn keine Regel trifft',
      severity: SEVERITY_FALLBACK,
      notApplicable: false,
    },
  ];

  const winnerIndex = candidates.findIndex(
    (candidate) => !candidate.notApplicable && candidate.severity !== null
  );

  const steps: SeverityResolutionStep[] = candidates.map((candidate, position) => ({
    ...candidate,
    isWinner: position === winnerIndex,
  }));

  return {
    severity: candidates[winnerIndex]?.severity ?? SEVERITY_FALLBACK,
    winningLevel: candidates[winnerIndex]?.level ?? 'fallback',
    steps,
  };
}
